import { useState, useEffect } from 'react'
import { Modal } from '@/components/ui/Modal'
import { FormField } from '@/components/ui/FormField'
import { useSessionStore } from '@/stores/sessionStore'
import { mockClients } from '@/data'
import { Calendar, Clock, MapPin, Target } from 'lucide-react'
import type { Session } from '@/types'

interface SessionFormData {
  clientId: string
  type: Session['type']
  date: string
  startTime: string
  endTime: string
  location: {
    type: 'gym' | 'home' | 'outdoor' | 'virtual'
    name?: string
    address?: string
    notes?: string
  }
  agenda: string[]
  goals: string[]
  preSessionNotes?: string
}

export function SessionFormModal() {
  const { 
    isAddSessionModalOpen, 
    isEditSessionModalOpen,
    closeAddSessionModal, 
    closeEditSessionModal,
    selectedSession,
    addSession,
    updateSession
  } = useSessionStore()

  const isEdit = isEditSessionModalOpen
  const isOpen = isAddSessionModalOpen || isEditSessionModalOpen

  const [formData, setFormData] = useState<SessionFormData>({
    clientId: '',
    type: 'training',
    date: '',
    startTime: '',
    endTime: '',
    location: {
      type: 'gym'
    },
    agenda: [''],
    goals: [''],
    preSessionNotes: ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Initialize form data when editing
  useEffect(() => {
    if (isEdit && selectedSession) {
      setFormData({
        clientId: selectedSession.clientId,
        type: selectedSession.type,
        date: selectedSession.date.toISOString().split('T')[0],
        startTime: selectedSession.startTime,
        endTime: selectedSession.endTime,
        location: selectedSession.location || { type: 'gym' },
        agenda: selectedSession.agenda || [''],
        goals: selectedSession.goals || [''],
        preSessionNotes: selectedSession.preSessionNotes || ''
      })
    } else if (!isEdit) {
      // Reset form for new session
      setFormData({
        clientId: '',
        type: 'training',
        date: '',
        startTime: '',
        endTime: '',
        location: {
          type: 'gym'
        },
        agenda: [''],
        goals: [''],
        preSessionNotes: ''
      })
    }
    setErrors({})
  }, [isEdit, selectedSession, isOpen])

  const handleClose = () => {
    if (isEdit) {
      closeEditSessionModal()
    } else {
      closeAddSessionModal()
    }
    setErrors({})
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.clientId) {
      newErrors.clientId = 'Please select a client'
    }

    if (!formData.date) {
      newErrors.date = 'Please select a date'
    }

    if (!formData.startTime) {
      newErrors.startTime = 'Please enter a start time'
    }

    if (!formData.endTime) {
      newErrors.endTime = 'Please enter an end time'
    }

    if (formData.startTime && formData.endTime && formData.startTime >= formData.endTime) {
      newErrors.endTime = 'End time must be after start time'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const calculateDuration = (start: string, end: string): number => {
    if (!start || !end) return 0
    
    const startMinutes = parseInt(start.split(':')[0]) * 60 + parseInt(start.split(':')[1])
    const endMinutes = parseInt(end.split(':')[0]) * 60 + parseInt(end.split(':')[1])
    
    return Math.max(0, endMinutes - startMinutes)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    const sessionData = {
      clientId: formData.clientId,
      coachId: 'coach-001', // This would come from auth context
      type: formData.type,
      date: new Date(formData.date),
      startTime: formData.startTime,
      endTime: formData.endTime,
      duration: calculateDuration(formData.startTime, formData.endTime),
      location: formData.location,
      agenda: formData.agenda.filter(item => item.trim() !== ''),
      goals: formData.goals.filter(goal => goal.trim() !== ''),
      status: 'scheduled' as const,
      preSessionNotes: formData.preSessionNotes || undefined
    }

    if (isEdit && selectedSession) {
      updateSession(selectedSession.id, sessionData)
    } else {
      addSession(sessionData)
    }
  }

  const updateArrayField = (
    field: 'agenda' | 'goals',
    index: number,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }))
  }

  const addArrayField = (field: 'agenda' | 'goals') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }))
  }

  const removeArrayField = (field: 'agenda' | 'goals', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? 'Edit Session' : 'Create New Session'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Client"
            error={errors.clientId}
            required
          >
            <select
              value={formData.clientId}
              onChange={(e) => setFormData(prev => ({ ...prev, clientId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a client</option>
              {mockClients
                .filter(client => client.status === 'active')
                .map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))
              }
            </select>
          </FormField>

          <FormField
            label="Session Type"
            required
          >
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as Session['type'] }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="training">Training Session</option>
              <option value="consultation">Consultation</option>
              <option value="assessment">Assessment</option>
              <option value="check-in">Check-in</option>
            </select>
          </FormField>
        </div>

        {/* Date and Time */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Date & Time
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label="Date"
              error={errors.date}
              required
            >
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </FormField>

            <FormField
              label="Start Time"
              error={errors.startTime}
              required
            >
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </FormField>

            <FormField
              label="End Time"
              error={errors.endTime}
              required
            >
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </FormField>
          </div>

          {formData.startTime && formData.endTime && (
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Duration: {calculateDuration(formData.startTime, formData.endTime)} minutes
            </p>
          )}
        </div>

        {/* Location */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Location
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Location Type">
              <select
                value={formData.location.type}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  location: { ...prev.location, type: e.target.value as any }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="gym">Gym</option>
                <option value="home">Home</option>
                <option value="outdoor">Outdoor</option>
                <option value="virtual">Virtual</option>
              </select>
            </FormField>

            <FormField label="Location Name">
              <input
                type="text"
                value={formData.location.name || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  location: { ...prev.location, name: e.target.value }
                }))}
                placeholder="e.g., Fitness Plus Gym"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </FormField>
          </div>

          <FormField label="Address">
            <input
              type="text"
              value={formData.location.address || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                location: { ...prev.location, address: e.target.value }
              }))}
              placeholder="123 Main St, City, State"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </FormField>

          <FormField label="Location Notes">
            <input
              type="text"
              value={formData.location.notes || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                location: { ...prev.location, notes: e.target.value }
              }))}
              placeholder="Any specific instructions..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </FormField>
        </div>

        {/* Session Agenda */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Session Agenda
          </h4>
          
          {formData.agenda.map((item, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={item}
                onChange={(e) => updateArrayField('agenda', index, e.target.value)}
                placeholder="Add agenda item..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {formData.agenda.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeArrayField('agenda', index)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          
          <button
            type="button"
            onClick={() => addArrayField('agenda')}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            + Add agenda item
          </button>
        </div>

        {/* Session Goals */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Session Goals
          </h4>
          
          {formData.goals.map((goal, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={goal}
                onChange={(e) => updateArrayField('goals', index, e.target.value)}
                placeholder="Add session goal..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {formData.goals.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeArrayField('goals', index)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          
          <button
            type="button"
            onClick={() => addArrayField('goals')}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            + Add goal
          </button>
        </div>

        {/* Pre-session Notes */}
        <FormField
          label="Pre-Session Notes"
        >
          <textarea
            value={formData.preSessionNotes || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, preSessionNotes: e.target.value }))}
            placeholder="Any notes or preparations for this session..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </FormField>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors"
          >
            {isEdit ? 'Update Session' : 'Create Session'}
          </button>
        </div>
      </form>
    </Modal>
  )
}